import React, { useState } from 'react';
import { langAttr, translate, type MessageKey, type MessageParams } from './catalog';
import {
  getNavigatorLanguages,
  resolveWidgetLocale,
  type WidgetLocale,
} from './resolve-locale';

export type WidgetI18nValue = {
  locale: WidgetLocale;
  t: (key: MessageKey, params?: MessageParams) => string;
  lang: string;
};

const englishFallback: WidgetI18nValue = {
  locale: 'en',
  t: (key, params) => translate('en', key, params),
  lang: 'en',
};

const WidgetI18nContext = React.createContext<WidgetI18nValue | null>(null);

function createI18nValue(locale: WidgetLocale): WidgetI18nValue {
  return {
    locale,
    t: (key, params) => translate(locale, key, params),
    lang: langAttr(locale),
  };
}

/**
 * Resolves the widget locale once on mount from `navigator.languages` and
 * never updates it afterwards (stable for the widget lifetime).
 */
export function WidgetI18nProvider({ children }: { children: React.ReactNode }) {
  const [value] = useState<WidgetI18nValue>(() => {
    const { locale } = resolveWidgetLocale(getNavigatorLanguages());
    return createI18nValue(locale);
  });

  return (
    <WidgetI18nContext.Provider value={value}>{children}</WidgetI18nContext.Provider>
  );
}

/**
 * Widget copy + locale. Outside a provider, falls back to English so existing
 * unit tests keep working without wrapping every render.
 */
export function useWidgetI18n(): WidgetI18nValue {
  return React.useContext(WidgetI18nContext) ?? englishFallback;
}
